<script setup lang="ts">
import { ref, computed, watch, nextTick, h } from 'vue';
import { NIcon, NTag, NText, type DataTableColumns, type DataTableRowData } from 'naive-ui';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import { IconCheckCircle, IconDocumentText, IconTray } from '@/components/design/icons';
import { contractFileTypeLabel } from '@/components/design/statusMaps';
import {
  addContractualPartyToContractFile,
  type ContractManagementContractInDb,
  type ContractManagementContractFileInDb,
} from '@/api';
import { ContractsService } from '@/services/contractManagement/contractsService';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import { logger } from '@/utils/logger';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

/** Links a contractual party (`entityUid`) to a contract file picked contract → file. */
interface Props {
  show: boolean;
  entityUid: string;
  // List of already assigned contract file UIDs to exclude
  assignedContractFileUids?: string[];
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'assigned', contractFileUid: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  assignedContractFileUids: () => [],
});

const emit = defineEmits<Emits>();
const message = useGaiaToast();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

// Contract selection state with expandable files
const contractSearchQuery = ref('');
const contracts = ref<ContractWithFiles[]>([]);
const contractLoading = ref(false);
const contractPage = ref(1);
const contractPageSize = ref(10);
const contractTotalCount = ref(0);
const expandedRowKeys = ref<string[]>([]);
const contractFilesCache = ref<
  Map<string, { files: ContractManagementContractFileInDb[]; total: number }>
>(new Map());
const selectedContractFile = ref<ContractManagementContractFileInDb | null>(null);

// Types for expandable contracts
interface ContractWithFiles extends ContractManagementContractInDb {
  isLeaf?: boolean;
  children?: ContractFileRow[];
  contractFilesCount?: number;
  contractFilesLoaded?: boolean;
  contractFilesLoading?: boolean;
}

interface ContractFileRow {
  uid: string;
  /** The type's label: "Unclassified" until the AI has read the file. */
  type: string;
  description: string | null;
  created_at: string;
  updated_at: string | null;
  isLeaf: boolean;
  contractUid?: string;
  vault_file?: { file_name?: string } | null;
  contractFile?: ContractManagementContractFileInDb;
  /** Already linked to this record — listed so the contract isn't empty, but not pickable. */
  linked: boolean;
}

// The search field owns its debounce and fires this at once when cleared.
function applyContractSearch(): void {
  contractPage.value = 1;
  void fetchContracts();
}

async function fetchContracts(): Promise<void> {
  contractLoading.value = true;
  try {
    const result = await ContractsService.searchContracts({
      text: contractSearchQuery.value,
      page: contractPage.value,
      pageSize: contractPageSize.value,
      sortKey: 'created_at',
      sortDirection: 'desc',
    });

    contracts.value = result.results.map(contract => ({
      ...contract,
      isLeaf: false,
      children: [],
      contractFilesCount: 0,
      contractFilesLoaded: false,
      contractFilesLoading: false,
    }));
    contractTotalCount.value = result.result_count;
  } catch (error) {
    logger.error('Error fetching contracts:', error);
    message.error('Failed to load contracts');
  } finally {
    contractLoading.value = false;
  }
}

// Load contract files for a contract when expanded
async function loadContractFiles(contract: ContractWithFiles): Promise<void> {
  if (contract.contractFilesLoading || contract.contractFilesLoaded) return;

  contract.contractFilesLoading = true;
  try {
    const result = await ContractFilesService.getContractFilesForContract(contract.uid, {
      text: '',
      page: 1,
      pageSize: 50, // API limit is 50
      sortKey: 'created_at',
      sortDirection: 'desc',
    });

    const files = result.results;

    contractFilesCache.value.set(contract.uid, { files, total: files.length });

    contract.contractFilesCount = files.length;
    contract.contractFilesLoaded = true;

    // Already-linked files stay in the list, disabled — hiding them made a
    // contract whose only file was linked expand to nothing.
    const fileRows: ContractFileRow[] = files.map(file => ({
      uid: file.uid,
      type: contractFileTypeLabel(file.type),
      description: file.description,
      created_at: file.created_at,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      updated_at: file.updated_at || null,
      isLeaf: true,
      contractUid: contract.uid,
      vault_file: file.vault_file
        ? {
            file_name: file.vault_file.current_revision?.file_name,
          }
        : null,
      contractFile: file,
      linked: props.assignedContractFileUids.includes(file.uid),
    }));

    // A contract with no files at all drops its expand arrow.
    const isLeaf = fileRows.length === 0;
    contract.children = isLeaf ? undefined : fileRows;
    contract.isLeaf = isLeaf;

    // Update the contract in the array for reactivity
    const index = contracts.value.findIndex(c => c.uid === contract.uid);
    if (index !== -1) {
      contracts.value[index] = { ...contract, children: contract.children, isLeaf };
    }
  } catch (error) {
    logger.error('Error fetching contract files:', error);
    message.error('Failed to load contract files');
    contract.children = [];
  } finally {
    contract.contractFilesLoading = false;
  }
}

// Handle contract expansion
async function handleExpandChange(keys: (string | number)[]): Promise<void> {
  expandedRowKeys.value = keys.map(k => String(k));

  // Load contract files for newly expanded contracts
  for (const key of expandedRowKeys.value) {
    const contract = contracts.value.find(c => c.uid === key);
    if (contract && !contract.contractFilesLoaded && !contract.contractFilesLoading) {
      await loadContractFiles(contract);
    }
  }
}

// Handle async loading when contract is expanded

async function handleLoad(row: DataTableRowData): Promise<void> {
  if (row.isLeaf || row.contractFilesLoaded || row.contractFilesLoading) {
    return;
  }

  const contract = contracts.value.find(c => c.uid === row.uid);
  if (!contract) {
    logger.error('Contract not found:', row.uid);
    return;
  }

  await loadContractFiles(contract);
}

// Handle assign
async function handleAssign(): Promise<void> {
  if (!selectedContractFile.value) {
    message.warning('Please select a contract file');
    return;
  }

  try {
    await addContractualPartyToContractFile({
      query: {
        contractual_party_uid: props.entityUid,
        contract_file_uid: selectedContractFile.value.uid,
      },
      throwOnError: true,
    });
    message.success('Contract file assigned to contractual party successfully');

    emit('assigned', selectedContractFile.value.uid);
    showModal.value = false;
  } catch (error) {
    logger.error('Error assigning contract file:', error);
    const errorMsg =
      error instanceof Error ? error.message : 'Failed to assign contract file. Please try again.';
    message.error(errorMsg);
  }
}

// Handle cancel/close
function handleCancel(): void {
  showModal.value = false;
}

// Reset modal state
function resetModal(): void {
  selectedContractFile.value = null;
  contractSearchQuery.value = '';
  contractPage.value = 1;
  expandedRowKeys.value = [];
  contractFilesCache.value.clear();
  contracts.value = [];
}

// Watch for modal opening
watch(showModal, newValue => {
  if (newValue) {
    resetModal();
    void nextTick(() => {
      void fetchContracts();
    });
  }
});

watch(contractPage, () => {
  if (contractPage.value > 0) {
    void fetchContracts();
  }
});

// Table columns for contracts with expandable files
const contractColumns = computed((): DataTableColumns<ContractWithFiles | ContractFileRow> => [
  {
    type: 'selection',
    // Only contract files are pickable, and not one already linked.
    disabled: (row: ContractWithFiles | ContractFileRow): boolean =>
      !('linked' in row) || row.linked,
  },
  {
    title: 'Name / File',
    key: 'name',
    width: 300,
    render(row): ReturnType<typeof h> {
      // Contract file row
      if ('linked' in row) {
        const fileRow = row;
        return h(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              paddingLeft: '32px',
            },
          },
          [
            h(NIcon, { size: 16, color: '#18a058' }, { default: () => h(IconDocumentText) }),
            h(
              'span',
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
              fileRow.vault_file?.file_name ||
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
                fileRow.description ||
                `File ${fileRow.uid.slice(0, 8)}`
            ),
            fileRow.linked ? h(NText, { depth: 3 }, { default: () => 'Already linked' }) : null,
          ]
        );
      }

      // Contract row
      const contract = row;
      return h('span', contract.name);
    },
  },
  {
    title: 'Type',
    key: 'type',
    width: 150,
    render(row): ReturnType<typeof h> {
      if ('linked' in row) {
        const fileRow = row;
        return h(NTag, { type: 'success', size: 'small' }, { default: () => fileRow.type });
      }
      const contract = row;
      return h(NTag, { type: 'info' }, { default: () => contract.contract_type?.name ?? '—' });
    },
  },
  {
    title: 'Description',
    key: 'description',
    ellipsis: {
      tooltip: true,
    },
    render(row): ReturnType<typeof h> {
      if ('linked' in row) {
        const fileRow = row;
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        return h('span', fileRow.description || '—');
      }
      const contract = row;
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      return h('span', contract.description || '—');
    },
  },
]);

// Pagination config
const contractPagination = computed(
  (): {
    page: number;
    pageSize: number;
    itemCount: number;
    showSizePicker: boolean;
    pageSizes: number[];
    onUpdatePage: (page: number) => void;
    onUpdatePageSize: (size: number) => void;
  } => ({
    page: contractPage.value,
    pageSize: contractPageSize.value,
    itemCount: contractTotalCount.value,
    showSizePicker: true,
    pageSizes: [10, 20, 50],
    onUpdatePage: (page: number): void => {
      contractPage.value = page;
    },
    onUpdatePageSize: (size: number): void => {
      contractPageSize.value = size;
      contractPage.value = 1;
    },
  })
);

/**
 * The whole row is the target, not just its arrow or checkbox: a contract row
 * expands / collapses, a file row picks / unpicks. Clicks on the arrow or the
 * checkbox are left to those controls so they don't act twice.
 */
function contractRowProps(row: ContractWithFiles | ContractFileRow): Record<string, unknown> {
  const isFile = 'linked' in row;
  if (isFile ? row.linked : row.isLeaf) return {};
  return {
    style: { cursor: 'pointer' },
    onClick: (event: MouseEvent): void => {
      const target = event.target as HTMLElement;
      if (target.closest('.n-checkbox, .n-data-table-expand-trigger')) return;
      if (isFile) {
        handleContractFileSelection(selectedContractFile.value?.uid === row.uid ? [] : [row.uid]);
        return;
      }
      const keys = expandedRowKeys.value.includes(row.uid)
        ? expandedRowKeys.value.filter(key => key !== row.uid)
        : [...expandedRowKeys.value, row.uid];
      void handleExpandChange(keys);
    },
  };
}

// Selected contract file keys
const selectedContractFileKeys = computed((): string[] => {
  if (!selectedContractFile.value) return [];
  return [selectedContractFile.value.uid];
});

// Handle contract file row selection
function handleContractFileSelection(keys: (string | number)[]): void {
  if (keys.length === 0) {
    selectedContractFile.value = null;
    return;
  }
  const fileUid = keys[0] as string;

  // Find the contract file in all contracts' children
  for (const contract of contracts.value) {
    if (contract.children) {
      const fileRow = contract.children.find(f => f.uid === fileUid);
      if (fileRow?.contractFile && !fileRow.linked) {
        selectedContractFile.value = fileRow.contractFile;
        return;
      }
    }
  }
}
</script>

<template>
  <NModal
    v-model:show="showModal"
    preset="card"
    title="Assign Contract File"
    style="width: 900px; max-width: 90vw"
    :mask-closable="false"
    class="assign-contract-file-modal"
  >
    <div class="modal-content">
      <!-- Contract Selection with Expandable Files -->
      <div class="step-content">
        <div class="step-header">
          <div class="step-title">
            <NIcon size="20" style="color: #3b82f6; margin-right: 8px">
              <IconTray />
            </NIcon>
            <NText strong style="font-size: 16px">Select Contract File</NText>
          </div>
          <NText depth="3" style="font-size: 13px; margin-top: 4px">
            Click a contract to see its files, then pick one
          </NText>
        </div>

        <div class="search-section">
          <gaia-search-input
            v-model:value="contractSearchQuery"
            variant="panel"
            size="medium"
            placeholder="Search contracts"
            :debounce="400"
            @search="applyContractSearch"
          />
        </div>

        <div class="table-section">
          <gaia-table
            :columns="contractColumns"
            :data="contracts"
            :loading="contractLoading"
            :pagination="contractPagination"
            :row-key="(row: ContractWithFiles | ContractFileRow) => row.uid"
            :expanded-row-keys="expandedRowKeys"
            :cascade="false"
            :indent="24"
            :default-expand-all="false"
            :row-props="contractRowProps"
            allow-checking-not-loaded
            :checked-row-keys="selectedContractFileKeys"
            @update:expanded-row-keys="handleExpandChange"
            @load="handleLoad"
            @update:checked-row-keys="handleContractFileSelection"
          />

          <NEmpty
            v-if="!contractLoading && contracts.length === 0"
            description="No contracts found"
            style="padding: 40px 0"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <NSpace justify="space-between">
        <div style="flex: 1"></div>
        <NSpace>
          <NButton @click="handleCancel">Cancel</NButton>
          <NButton type="primary" :disabled="!selectedContractFile" @click="handleAssign">
            <template #icon>
              <NIcon><IconCheckCircle /></NIcon>
            </template>
            Assign
          </NButton>
        </NSpace>
      </NSpace>
    </template>
  </NModal>
</template>

<style scoped>
.modal-content {
  display: flex;
  flex-direction: column;
  min-height: 500px;
  max-height: 700px;
}

.step-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
}

.step-header {
  flex-shrink: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.step-title {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.search-section {
  flex-shrink: 0;
}

.table-section {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

:deep(.n-data-table) {
  max-height: 400px;
  overflow-y: auto;
}

:deep(.n-data-table .n-data-table-tbody tr) {
  transition: background-color 0.2s ease;
}

:deep(.n-data-table .n-data-table-tbody tr:hover) {
  background-color: #f9fafb;
}

:deep(.n-data-table .n-data-table-tbody tr.contract-file-row) {
  cursor: pointer;
}

:deep(.n-data-table .n-data-table-tbody tr.contract-file-row:hover) {
  background-color: #f0f9ff;
}
</style>
